import { defineStore } from 'pinia';
import { JoinWithLifestyleService as _joinWithLifestyleService } from '@/services/store/join-with-lifestyle.service';
import { ref } from 'vue';
import { IPartyMatchRequest } from '@/interfaces/entities/party-match-request.interface';
import { format, parse, compareAsc, addYears } from 'date-fns';
import { RACQAddressResult } from '@/interfaces/entities';
import { CardTypes } from '@/enums/payment-types.enum';
import { GenericInvoiceEmailRequest } from '@/interfaces/entities/generic-invoice-email-request.interface';

interface PersonalDetails extends Partial<IPartyMatchRequest> {
  title?: string;
  homeAddress: RACQAddressResult;
  postalAddress: RACQAddressResult;
  checkPostalAddress: boolean;
}

export const useJoinWithLifestyleStore = defineStore('JoinWithLifestyle', () => {
  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const initialConfig = ref<
    Partial<{
      price: number;
      priceSuffix: string;
      productName: string;
      secondDisclaimer: string;
    }>
  >({});

  const partyDetails = ref<PersonalDetails>({
    homeAddress: {},
    postalAddress: {},
    checkPostalAddress: false,
  });
  const paymentReceipt = ref<{
    prn: string;
    assetId: string;
    invoiceId: string;
    quoteId: string;
    result?: string;
    isNewMember?: boolean;
  }>();
  const creditCardDetails = ref<Record<string, string>>({});

  const mapPersonalDetails = (formData: Record<string, any>) => {
    partyDetails.value.title = formData.fullname?.title;
    partyDetails.value.firstName = formData.fullname?.firstName;
    partyDetails.value.lastName = formData.fullname?.lastName;
    partyDetails.value.phoneNumber = formData?.contactNumber;
    partyDetails.value.dateOfBirth = format(parse(formData?.dateOfBirth, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd', {});
    partyDetails.value.emailAddress = formData?.email;
    partyDetails.value.postalAddressDpid = formData.postalAddress?.dpid;

    partyDetails.value.homeAddress = formData.homeAddress;

    if (!formData.checkPostalAddress) {
      partyDetails.value.postalAddress = partyDetails.value?.homeAddress || {};
    } else {
      partyDetails.value.postalAddress = formData.postalAddress;
    }

    // DSQ-1917 there is an edge case where manual addresses can cause the postalAddress to be {} and break stuff down stream,
    // null checks do not pick up an object with no properties, remove once manual address defect is fixed
    if (Object.keys(partyDetails.value.postalAddress).length === 0) {
      partyDetails.value.postalAddress = partyDetails.value?.homeAddress;
    }
  };

  const fetchConfig = async () => {
    const res = await _joinWithLifestyleService.getJoinLifestyleFormInitialContent();
    initialConfig.value = res;
  };

  const checkEligibility = async () => {
    const res = await _joinWithLifestyleService.checkLifestyleIsEligible({
      firstName: partyDetails.value.firstName!,
      lastName: partyDetails.value.lastName!,
      dateOfBirth: partyDetails.value.dateOfBirth!,
      phoneNumber: partyDetails.value.phoneNumber!,
      emailAddress: partyDetails.value.emailAddress!,
      postalAddressDpid: partyDetails.value.postalAddressDpid!,
    });
    if (res == null) throw 'Party match failed';
    return res;
  };

  const mapPaymentDetails = (formData: Record<string, any>) => {
    creditCardDetails.value = {
      cardToken: formData.cardToken,
      cardHolder: formData.cardHolder,
      MaskedCardNumber: formData.MaskedCardNumber,
      cardExpiry: formData.cardExpiry,
      cardType: formData.cardType,
      autoRenew: formData.autoRenew,
    };
  };

  const submitPurchase = async () => {
    try {
      const homeAddress = partyDetails.value.homeAddress;
      const postalAddress = partyDetails.value.postalAddress;

      const basePayload = {
        firstName: partyDetails.value.firstName!,
        lastName: partyDetails.value.lastName!,
        dateOfBirth: partyDetails.value.dateOfBirth!,
        phoneNumber: partyDetails.value.phoneNumber!,
        emailAddress: partyDetails.value.emailAddress!,
        postalAddressDpid: partyDetails.value.postalAddressDpid!,
        title: partyDetails.value.title,
        homeAddress: {
          dpid: homeAddress?.dpid,
          unit: homeAddress?.unitNumber,
          buildingLevel: homeAddress.buildingLevel,
          streetNumber: homeAddress?.streetNumber,
          street: homeAddress?.street,
          suburb: homeAddress?.suburb,
          postcode: homeAddress?.postcode,
          state: homeAddress?.state,
          country: homeAddress?.countryISO,
        },
        postalAddress: {
          postalDeliveryType: postalAddress?.postalDeliveryType,
          dpid: postalAddress?.dpid,
          unit: postalAddress?.unitNumber,
          buildingLevel: postalAddress.buildingLevel,
          streetNumber: postalAddress?.streetNumber,
          street: postalAddress?.street,
          suburb: postalAddress?.suburb,
          postcode: postalAddress?.postcode,
          state: postalAddress?.state,
          country: postalAddress?.countryISO,
        },
        ...(paymentReceipt.value || {}),
      };

      const isWalletPayment = [CardTypes.GooglePay, CardTypes.ApplePay].includes(
        creditCardDetails.value.cardType as CardTypes
      );

      const payload = isWalletPayment
        ? {
            ...basePayload,
            walletToken: creditCardDetails.value.cardToken,
            walletProvider: creditCardDetails.value.cardType,
          }
        : {
            ...basePayload,
            cardToken: creditCardDetails.value.cardToken,
            // AutoRenew Exclusive Fields
            cardHolder: creditCardDetails.value.cardHolder,
            MaskedCardNumber: creditCardDetails.value.MaskedCardNumber,
            cardExpiry: creditCardDetails.value.cardExpiry,
            cardType: creditCardDetails.value.cardType,
            autoRenew: creditCardDetails.value.autoRenew,
          };

      const result = await _joinWithLifestyleService.submitPurchase(payload);
      paymentReceipt.value = result;
      return result;
    } catch {
      return null;
    }
  };

  function isUnderAge() {
    if (!partyDetails.value.dateOfBirth) return;
    const dob = parse(partyDetails.value.dateOfBirth, 'yyyy-MM-dd', new Date());
    return compareAsc(new Date(), addYears(dob, 18)) < 0;
  }

  const sendEmail = async (toAddress: string) => {
    const emailRequest: GenericInvoiceEmailRequest = {
      receiptNumber: paymentReceipt.value?.prn,
      amount: initialConfig.value.price!,
      product: 'RACQ Lifestyle*',
      emailAddress: partyDetails.value.emailAddress!,
      firstName: partyDetails.value.firstName!,
      paymentMethod: creditCardDetails.value.cardType,
      phoneNumber: partyDetails.value.phoneNumber!,
      toAddress: toAddress,
      isJoinOnRoad: false,
    };

    await _joinWithLifestyleService.sendGenericInvoiceEmail(emailRequest);
  };

  return {
    paymentReceipt,
    partyDetails,
    initialConfig,
    fetchConfig,
    checkEligibility,
    mapPersonalDetails,
    mapPaymentDetails,
    submitPurchase,
    isUnderAge,
    sendEmail,
  };
});
